// view-referal.component.ts
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// SERVICES
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { ReferralsService, GetReferredAccountsRequest } from '../referrals.service';
import { RequestsService } from 'src/app/core/services/http/requests.service';

// CONSTANTS
import { Urls } from 'src/app/lib/urls';
import {
  REFERRAL_TABLE_SUB_HEADERS,
  ACCOUNT_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from 'src/app/lib/constants/referral.constants';

export interface ViewReferalData {
  user: {
    firstName:     string;
    middleName:    string;
    lastName:      string;
    phoneNumber:   string; 
    email:         string;
    dateCreated:   string;
    status:        string;
    accountType:   string;
    tierId:        string;
    referralCount: number;
  };
}

@Component({
  selector: 'app-view-referal',
  templateUrl: './view-referal.component.html',
  styleUrl: './view-referal.component.scss',
})
export class ViewReferalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public form!: UntypedFormGroup;
  searchForm!: UntypedFormGroup;
  limitForm!: UntypedFormGroup;

  // ─── Dropdown Options ──────────────────────────────────────────────────────

  statusOptions      = STATUS_OPTIONS;
  accountTypeOptions = ACCOUNT_TYPE_OPTIONS;

  tierOptions: { label: string; value: any }[] = [];

  // ─── Dropdown State ────────────────────────────────────────────────────────

  statusType: any       = null;
  statusType_text: any  = null;
  accountType: any      = null;
  accountType_text: any = null;
  tier: any             = null;
  tier_text: any        = null;

  // ─── Table ────────────────────────────────────────────────────────────────── 

  tableSubHeader = REFERRAL_TABLE_SUB_HEADERS.slice(0, -1);

  // ─── Data ──────────────────────────────────────────────────────────────────

  itemLists: any    = [];
  submitted         = false;
  isLoading         = false;
  filterFields: GetReferredAccountsRequest | null = null;
  fileName          = '';

  // ─── Pagination ────────────────────────────────────────────────────────────

  size                         = 10;
  total_pages                  = 0;
  current_page                 = 1;
  pages_array: number[] | null = [];
  total                        = 0;

  // ─── Search & Limit ────────────────────────────────────────────────────────

  search_keyword = '';
  searchKey: any  = null;
  limitKey: any   = null;

  // ─── Sorting ───────────────────────────────────────────────────────────────

  sort_column               = 'dateCreated';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Constructor ───────────────────────────────────────────────────────────

  constructor(
    public dialogRef: MatDialogRef<ViewReferalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ViewReferalData,
    private fb: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private referralService: ReferralsService,
    private request: RequestsService
  ) {
    this.initializeForm();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadTierOptions();
    this.initialLoad();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Load Tier Options dynamically ────────────────────────────────────────

  private loadTierOptions(): void {
    this.referralService
      .getAccountsTiers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.result) {
            this.tierOptions = (res.result as any[]).map((t: any) => ({
              label: `Tier ${t.tierId} (${t.tierName})`,
              value: t.tierId,  
            }));
          }
        },
        error: () => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs`,
            'Something went wrong'
          );
        },
      });
  }

  // ─── Form Initialization ───────────────────────────────────────────────────

  initializeForm(): void {
    this.form = this.fb.group({
      firstName:   [null],
      middleName:  [null],
      lastName:    [null],
      phoneNumber: [null],
    });

    this.searchForm = this.fb.group({ search: [''] });
    this.limitForm  = this.fb.group({ limit: [this.size] });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page   = 1;
      this.search_keyword = this.searchForm.value.search;
      if (this.filterFields) { this.get_filtered_list(); }
    }, 500);

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size         = this.limitForm.value.limit;
      if (this.filterFields) { this.get_filtered_list(); }
    }, 500);
  }

  // ─── Submit ────────────────────────────────────────────────────────────────

  submit(): void {
    this.isLoading    = true;
    this.current_page = 1;
    this.pages_array  = [];

    const params: GetReferredAccountsRequest = {
      referrerNumber: this.data.user.phoneNumber,
      firstName:   this.form.controls['firstName'].value   || null,
      middleName:  this.form.controls['middleName'].value  || null,
      lastName:    this.form.controls['lastName'].value    || null,
      phoneNumber: this.form.controls['phoneNumber'].value || null,
      status:      this.statusType  || null,
      accountType: this.accountType || null,
      tierId:      this.tier        || null,
      sortBy:      { [this.sort_column]: this.sort_type },
    };

    this.filterFields = params;
    localStorage.setItem('referredList', JSON.stringify(params));

    this.referralService
      .getReferredAccounts(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.handleListResponse(res);
          this.submitted = true;
          this.resetFilters();  
        },
        error: (err) => {
          this.toastr.error('Failed to fetch referred accounts.');
          this.isLoading = false;
        },
      });
  }

  // ─── Resets  ─────────────────────────────────────────────────────────
  private resetFilters(): void {
    this.form.reset({
      firstName:   null,
      middleName:  null,
      lastName:    null,
      phoneNumber: null,
    });
    this.statusType       = null;
    this.statusType_text  = null;
    this.accountType      = null;
    this.accountType_text = null;
    this.tier             = null;
    this.tier_text        = null;
  }

  private resetAll(): void {
    this.resetFilters();
    this.filterFields     = null;
    this.search_keyword   = '';
    this.searchForm.reset({ search: '' });
    this.limitForm.reset({ limit: this.size });
    this.itemLists        = [];
    this.submitted        = false;
    this.isLoading        = false;
    this.total            = 0;
    this.total_pages      = 0;
    this.current_page     = 1;
    this.pages_array      = [];
    this.sort_column      = 'dateCreated';
    this.sort_type        = 'ASC';
    localStorage.setItem('referredList', JSON.stringify({
      referrerNumber: this.data.user.phoneNumber
    }));
  }


  refresh(): void {
    this.resetAll();
    this.initialLoad();
  }


  // ─── Filtered List ─────────────────────────────────────────────────────────

  get_filtered_list(): void {
    this.isLoading   = true;
    this.pages_array = [];

    const params: GetReferredAccountsRequest = {
      ...this.filterFields,
      referrerNumber: this.data.user.phoneNumber,   // always keep referrerNumber
      search: this.search_keyword || null,
      sortBy: { [this.sort_column]: this.sort_type },
    };

    localStorage.setItem('referredList', JSON.stringify(params));

    this.referralService
      .getReferredAccounts(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.handleListResponse(res),
        error: () => {
          this.toastr.error('Failed to fetch referred accounts.');
          this.isLoading = false;
        },
      });
  }

  // ─── Response Handler ──────────────────────────────────────────────────────


  private initialLoad(): void {
    this.isLoading    = true;
    this.current_page = 1;
    this.pages_array  = [];

    const params: GetReferredAccountsRequest = {
      referrerNumber: this.data.user.phoneNumber,
      sortBy: { [this.sort_column]: this.sort_type },
    };

    this.filterFields = params;

    this.referralService
      .getReferredAccounts(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.handleListResponse(res);
          this.submitted = true;
        },
        error: () => {
          this.toastr.error('Failed to fetch referred accounts.');
          this.isLoading = false;
        },
      });
  }

  private handleListResponse(res: any): void {
    if (res?.result) {
      this.itemLists    = res.result.data         ?? [];
      this.total        = res.result.totalItems   ?? 0;
      this.total_pages  = res.result.totalPages   ?? 0;
      this.current_page = res.result.currentPage  ?? this.current_page;
      this.pages_array  = this.paginationHelper.generatePageArray(this.current_page, this.total_pages);
    } else {
      this.itemLists = [];
    }
    this.isLoading = false;
  }

  // ─── Export ────────────────────────────────────────────────────────────────
  async print(): Promise<void> {
    this.spinner.show().catch((err) => console.error(err));

    const current = new Date();
    this.fileName = current.toLocaleDateString() + '-referred-accounts.xlsx';
    const params  = JSON.parse(localStorage.getItem('referredList') ?? 'null');

    try {
      await this.request.postBlob(Urls.exportReferredAccounts, params).then((report: any) => {
        const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
        const url  = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = this.fileName;
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      });
      this.spinner.hide().catch((err) => console.error(err));
    } catch (err) {
      this.spinner.hide().catch((e) => console.error(e));
    }
  }

  // ─── Dropdown Setters ──────────────────────────────────────────────────────

  setStatusType(text: string, value: string): void {
    this.statusType      = value;
    this.statusType_text = text;
  }

  setAccountType(text: string, value: string): void {
    this.accountType      = value;
    this.accountType_text = text;
  }

  setTier(text: string, value: any): void {
    this.tier      = value;
    this.tier_text = text;
  }

  // ─── Table Controls ────────────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) { this.searchList(); }
  }

  searchList(): void {
    this.current_page   = 1;
    this.search_keyword = this.searchForm.value.search;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  // ─── Sorting & Pagination ──────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column, this.sort_type, sort_column, sort_type,
    );
    this.sort_column = column;
    this.sort_type   = type;
    if (this.submitted && this.filterFields) { this.get_filtered_list(); }
  }

  setPage(num: number): void {
    this.current_page = num;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  // ─── Close ─────────────────────────────────────────────────────────────────

  closeDialog(): void {
    this.dialogRef.close();
  }
}
